import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components';
import { ApiClient } from '@utils/apiClient';
import { useToast } from '@contexts/useToast';
import { handleApiResult } from '@utils/apiErrorHandler';
import { useAuth } from '@pages/Auth';
import { AgentTestOverlay } from './AgentTestOverlay';

interface TestAgentButtonWebRTCProps {
  agentId: string;
  agentName: string;
  disabled?: boolean;
  testing?: boolean;
  onTestingChange?: (testing: boolean) => void;
  stopTestRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  onSave?: () => Promise<void>;
}

interface WebRTCConnectResponse {
  sdp_answer: string;
  conversation_id: string;
  call_id: string;
  intro_message?: string;
}

export const TestAgentButtonWebRTC: React.FC<TestAgentButtonWebRTCProps> = ({
  agentId,
  agentName: _agentName,
  disabled = false,
  testing: _externalTesting,
  onTestingChange: _onTestingChange,
  stopTestRef,
  onSave,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { organization } = useAuth();

  const [internalTesting, setInternalTesting] = useState(false);
  const testing = _externalTesting !== undefined ? _externalTesting : internalTesting;
  const setTesting = (value: boolean) => {
    if (_externalTesting === undefined) {
      setInternalTesting(value);
    }
    _onTestingChange?.(value);
  };

  const [connectionStatus, setConnectionStatus] = useState<
    'idle' | 'connecting' | 'connected' | 'disconnected'
  >('idle');
  const [showOverlay, setShowOverlay] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const conversationIdRef = useRef<string | null>(null);

  const startWebRTCSession = async () => {
    try {
      // Save agent settings before starting test
      if (onSave) {
        try {
          await onSave();
          showToast({
            type: 'success',
            title: 'Settings saved',
            message: 'Agent settings saved before test',
            duration: 2000,
          });
        } catch (error) {
          showToast({
            type: 'error',
            title: 'Save failed',
            message: error instanceof Error ? error.message : 'Failed to save settings',
          });
          return;
        }
      }

      setTesting(true);
      setConnectionStatus('connecting');
      setShowOverlay(true); // Show overlay when starting test

      // 1. WebRTC PeerConnection erstellen
      const pc = new RTCPeerConnection();
      peerConnectionRef.current = pc;

      // 2. Mikrofon-Audio hinzufügen
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 24000,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        mediaStreamRef.current = stream;

        stream.getAudioTracks().forEach(track => {
          pc.addTrack(track, stream);
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Microphone access denied. Please allow microphone access and try again.';
        showToast({
          type: 'error',
          title: t('agents.microphoneAccessError'),
          message: errorMessage,
        });
        setTesting(false);
        setConnectionStatus('idle');
        setShowOverlay(false); // Hide overlay on error
        return;
      }

      // 3. Audio-Wiedergabe einrichten
      const audioElement = new Audio();
      audioElement.autoplay = true;
      audioElementRef.current = audioElement;

      pc.ontrack = event => {
        if (event.streams.length > 0) {
          audioElement.srcObject = event.streams[0];
        }
      };

      // 4. DataChannel für Events
      const dc = pc.createDataChannel('realtime-channel');
      dataChannelRef.current = dc;

      // Variable to store intro_message (will be set after connect response)
      let introMessage: string | undefined = undefined;

      dc.onopen = () => {
        setConnectionStatus('connected');
        showToast({
          type: 'success',
          title: t('agents.testSessionConnected'),
          message: t('agents.testSessionConnectedMessage'),
          duration: 3000,
        });

        // Trigger welcome message with intro_message instructions
        try {
          if (introMessage) {
            dc.send(
              JSON.stringify({
                type: 'response.create',
                response: {
                  instructions: `Say exactly this greeting to start the conversation: '${introMessage}'. Use the exact words provided. After the greeting, continue naturally following the system instructions.`,
                },
              })
            );
          } else {
            dc.send(JSON.stringify({ type: 'response.create' }));
          }
        } catch (err) {
          // Failed to send response.create
        }
      };

      dc.onmessage = event => {
        try {
          const message = JSON.parse(event.data);
          handleRealtimeEvent(message);
        } catch (err) {
          // Failed to parse message
        }
      };

      dc.onerror = () => {
        // Data channel error
      };

      dc.onclose = () => {
        setConnectionStatus('disconnected');
      };

      // 5. Connection State Monitoring
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          setConnectionStatus('disconnected');
          showToast({
            type: 'error',
            title: t('agents.testSessionError'),
            message: 'Connection lost',
          });
        }
      };

      pc.oniceconnectionstatechange = () => {
        // ICE connection state changed
      };

      // 6. SDP Offer erstellen
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // 7. SDP an Backend senden (proxied über /webrtc-connect/)
      // Backend startet automatisch WebSocket Observer für Tool-Unterstützung
      const connectResponse = await ApiClient.post(`/agents/agents/${agentId}/webrtc-connect/`, {
        sdp_offer: offer.sdp,
      });

      const result = await handleApiResult<WebRTCConnectResponse>(
        connectResponse,
        'Failed to establish WebRTC connection'
      );

      if (!result.success || !result.data) {
        const errorMessage = result.errors?.generalError || 'Failed to connect';
        showToast({
          type: 'error',
          title: t('agents.testSessionError'),
          message: errorMessage,
        });
        setTesting(false);
        setConnectionStatus('idle');
        setShowOverlay(false); // Hide overlay on connect error
        cleanup();
        return;
      }

      const { sdp_answer, conversation_id, intro_message } = result.data;

      // Store conversation ID for billing on cleanup
      conversationIdRef.current = conversation_id;

      // Store intro message for use in onopen callback
      introMessage = intro_message;

      // 8. SDP Answer setzen
      await pc.setRemoteDescription({ type: 'answer', sdp: sdp_answer });

      // Store stop function
      if (stopTestRef) {
        stopTestRef.current = stopWebRTCSession;
      }
    } catch (error) {
      showToast({
        type: 'error',
        title: t('agents.testSessionError'),
        message: error instanceof Error ? error.message : 'Failed to start WebRTC session',
      });
      setTesting(false);
      setConnectionStatus('idle');
      setShowOverlay(false); // Hide overlay on error
      cleanup();
    }
  };

  const handleRealtimeEvent = (message: { type: string; [key: string]: unknown }) => {
    // Event-Handler kompatibel mit bestehender WebSocket-Implementierung
    switch (message.type) {
      case 'input_audio_buffer.speech_started':
        break;
      case 'input_audio_buffer.speech_stopped':
        break;
      case 'output_audio_buffer.started':
        setIsSpeaking(true);
        break;
      case 'output_audio_buffer.stopped':
        setIsSpeaking(false);
        break;
      case 'response.output_audio_transcript.delta':
        break;
      case 'response.output_audio_transcript.done':
        break;
      case 'conversation.item.input_audio_transcription.completed':
        break;
      case 'error':
        showToast({
          type: 'error',
          title: t('agents.testSessionError'),
          message: JSON.stringify(message),
        });
        break;
      default:
        break;
    }
  };

  const stopWebRTCSession = async () => {
    // Send call-end notification for billing (50% test pricing)
    if (conversationIdRef.current) {
      try {
        await ApiClient.post('/agents/agents/webrtc-call-end/', {
          conversation_id: conversationIdRef.current,
        });
      } catch (err) {
        // Don't show error to user - billing is backend concern
      }
      conversationIdRef.current = null;
    }

    cleanup();
    setTesting(false);
    setConnectionStatus('idle');
    setShowOverlay(false); // Hide overlay when stopping
    setIsSpeaking(false);

    if (stopTestRef) {
      stopTestRef.current = null;
    }
  };

  const cleanup = () => {
    // Close data channel
    if (dataChannelRef.current) {
      try {
        dataChannelRef.current.close();
      } catch (err) {
        // Error closing data channel
      }
      dataChannelRef.current = null;
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      try {
        peerConnectionRef.current.close();
      } catch (err) {
        // Error closing peer connection
      }
      peerConnectionRef.current = null;
    }

    // Stop media tracks
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    // Stop audio playback
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.srcObject = null;
      audioElementRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  const getButtonText = () => {
    if (connectionStatus === 'connecting') {
      return t('agents.testConnecting');
    }
    if (connectionStatus === 'connected') {
      return t('agents.testStop');
    }
    return t('agents.testAgent');
  };

  const hasInsufficientBalance = organization?.has_sufficient_balance === false;
  const isButtonDisabled = disabled || !agentId || hasInsufficientBalance;

  const handleClick = async () => {
    if (testing) {
      // Use stopTestRef if available (for synchronized stop from either button)
      if (stopTestRef?.current) {
        await stopTestRef.current();
      } else {
        await stopWebRTCSession();
      }
    } else {
      await startWebRTCSession();
    }
  };

  const handleOverlayClose = async () => {
    await stopWebRTCSession();
  };

  return (
    <>
      <Button
        variant={testing ? 'danger' : 'gradient-multi'}
        size="md"
        onClick={handleClick}
        disabled={isButtonDisabled}
        loading={connectionStatus === 'connecting'}
        leftIcon={<span className="material-icons text-sm">{testing ? 'stop' : 'mic'}</span>}
      >
        {getButtonText()}
      </Button>

      <AgentTestOverlay
        isOpen={showOverlay}
        isConnected={connectionStatus === 'connected'}
        isSpeaking={isSpeaking}
        onClose={handleOverlayClose}
      />
    </>
  );
};
